/**
 * Integrations Component
 *
 * This component manages the integration cards and functionality for connecting
 * various third-party services to the application. It handles:
 *
 * - Personal integrations (Google Calendar, Microsoft Calendar, Zoom)
 * - Organization-wide integrations (Slack, Gong)
 * - OAuth flows for connecting/disconnecting integrations
 * - Status tracking for all integrations
 * - Dialog management for integration-specific actions
 *
 * The component is responsible for:
 * 1. Fetching and displaying integration status
 * 2. Handling OAuth redirects and token exchanges
 * 3. Managing connection/disconnection of integrations
 * 4. Providing UI for users to interact with integrations
 */

import React, { useContext, useEffect, useState, useRef, type ReactPortal } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { useWorkspace } from '../../../context/WorkspaceContext'
import { RemoteConfigContext } from '../../../context/RemoteConfigContext'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { Card } from './card'
import { RequestCard } from './requestCard'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SwitchCalendarDialog } from './switchCalendarDialog'
import { GongImportDialog } from './gongImportDialog'
import { handleOAuthError } from '../../../utils/oauthHelpers'
import { createPortal } from 'react-dom'
import { GoogleIcon } from '../../icons/Google'
import { MicrosoftIcon } from '../../icons/Microsoft'
import { ZoomIcon } from '../../icons/Zoom'
import { SlackIcon } from '../../icons/Slack'
import { GongIcon } from '../../icons/Gong'
import {
  IntegrationKeys,
  IntegrationKeysMap,
  IntegrationKey,
  personalIntegrations,
} from '../../../constants/integrations'

export function Integrations() {
  const { user, setUser } = useContext(AuthContext)
  const { config } = useContext(RemoteConfigContext)
  const { workspace: workspaceData, isPartner, refreshWorkspace } = useWorkspace()

  /**
   * Organization-wide integrations that apply to the entire workspace
   * These are managed at the workspace level rather than user level
   */
  const organizationWideIntegrations: IntegrationKey[] = []
  if (config.slack) organizationWideIntegrations.push(IntegrationKeys.Slack)
  // Disable Gong temporarily
  // if (config.gong) organizationWideIntegrations.push(IntegrationKeys.Gong)

  // Array of connected integration keys (e.g., 'G', 'S')
  const [connected, setConnected] = useState([])

  // List of available products for integration requests
  const [products, setProducts] = useState([])

  // Controls which calendar switch dialog is shown ('googleCalendar', 'microsoftCalendar', or empty string)
  const [showSwitchDialog, setShowSwitchDialog] = useState('')

  // Controls visibility of the Gong import dialog
  const [showGongImportDialog, setShowGongImportDialog] = useState(false)

  // Loading state for the entire component
  const [isLoading, setIsLoading] = useState(true)

  // Tracks which integrations are currently in the process of connecting/disconnecting
  const [isFetchingStatus, setIsFetchingStatus] = useState({})

  // Prevents duplicate processing of OAuth codes
  const processedCodes = useRef(new Set())

  // Prevents showing multiple toasts for the same operation
  const toastShown = useRef(false)

  const [saving, setSaving] = useState(false)

  // Centralized state for tracking connection status of all integrations
  const [integrationStatus, setIntegrationStatus] = useState({
    GoogleCalendar: false,
    MicrosoftCalendar: false,
    Zoom: false,
    Slack: false,
    Gong: false,
  })

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const signInMethod = localStorage.getItem('signin_method')
  const customFetch = useCustomFetch()

  // Tracks if we've already checked for pending Gong actions
  const gongActionsChecked = useRef(false)

  // Tracks if integrations have been initialized
  const integrationsInitialized = useRef(false)

  // Tracks if integration status has been initialized
  const statusInitialized = useRef(false)

  // Tracks if we've run the forced initialization
  const forcedInitializationRun = useRef(false)

  // Initialize all integrations on component mount
  useEffect(() => {
    if (user?.active_workspace) {
      initializeIntegrations()
      setIsLoading(false)
    }
  }, [user, workspaceData]) // Add workspaceData from context as a dependency

  // Add a direct check for both initialization methods - fixed to prevent infinite loop
  useEffect(() => {
    // Skip if we don't have both user and workspace data or if we've already done this
    if (!user?.integrations || !workspaceData?.integrations || forcedInitializationRun.current) {
      return
    }

    // This should be a single reliable method to set integration status
    const statusMap = { GoogleCalendar: false, MicrosoftCalendar: false, Zoom: false, Slack: false, Gong: false }

    // Set user integration status
    if (user.integrations.googleCalendar === true) statusMap.GoogleCalendar = true
    if (user.integrations.microsoftCalendar === true) statusMap.MicrosoftCalendar = true
    if (user.integrations.zoom === true) statusMap.Zoom = true

    // Set workspace integration status
    if (workspaceData.integrations.slack?.connected === true) statusMap.Slack = true
    if (workspaceData.integrations.gong?.connected === true) statusMap.Gong = true

    // Update the status
    setIntegrationStatus(statusMap)

    // Update connected and disconnected arrays
    const connected = Object.entries(statusMap)
      .filter(([_, isConnected]) => isConnected)
      .map(([key]) => key)

    setConnected(connected)

    // Mark that we've run the forced initialization
    forcedInitializationRun.current = true
  }, [user, workspaceData])

  /**
   * Initializes the integration status based on user and workspace data
   *
   * This function:
   * 1. Creates a status map for all integrations (initialized to false)
   * 2. Processes user integrations (personal ones like Google Calendar, Microsoft Calendar, Zoom)
   * 3. Processes workspace integrations (organization-wide ones like Slack and Gong)
   * 4. Updates the integrationStatus state with the combined status
   * 5. Updates the connected/disconnected arrays for UI rendering
   *
   * The function handles both object and array formats for backward compatibility
   * and ensures all integrations have a defined status.
   */
  const initializeIntegrations = () => {
    const connectedArray: IntegrationKey[] = []
    const disconnectedArray: IntegrationKey[] = []
    // Start with all keys initialized to false
    const statusMap = { GoogleCalendar: false, MicrosoftCalendar: false, Zoom: false, Slack: false, Gong: false }

    // Create reverse mapping from integration name to key
    const reverseIntegrationKeys = Object.entries(IntegrationKeys).reduce((acc, [key, value]) => {
      acc[value] = key
      return acc
    }, {})

    // Process user integrations (personal integrations)
    if (user.integrations) {
      for (const [integrationName, isConnected] of Object.entries(user.integrations)) {
        const frontendKey = reverseIntegrationKeys[integrationName]

        if (frontendKey) {
          // Store in status map
          statusMap[frontendKey] = !!isConnected

          if (isConnected === true) {
            connectedArray.push(frontendKey)
          } else {
            disconnectedArray.push(frontendKey)
          }
        }
      }
    }

    // Process organization-wide integrations from workspace object
    // Get workspace integrations from workspaceData or user.workspaces
    const workspaceIntegrations =
      workspaceData?.integrations || user.workspaces?.[user.active_workspace]?.integrations || {}

    // Check if workspace integrations is an object and not an array
    if (typeof workspaceIntegrations === 'object' && !Array.isArray(workspaceIntegrations)) {
      // Check if Slack is connected
      if (workspaceIntegrations.slack?.connected === true) {
        statusMap.Slack = true
        if (!connectedArray.includes(IntegrationKeys.Slack)) {
          connectedArray.push(IntegrationKeys.Slack)
        }
      } else {
        if (!disconnectedArray.includes(IntegrationKeys.Slack)) {
          disconnectedArray.push(IntegrationKeys.Slack)
        }
      }

      // Check if Gong is connected
      if (workspaceIntegrations.gong?.connected === true) {
        statusMap.Gong = true
        if (!connectedArray.includes(IntegrationKeys.Gong)) {
          connectedArray.push(IntegrationKeys.Gong)
        }
      } else {
        if (!disconnectedArray.includes(IntegrationKeys.Gong)) {
          disconnectedArray.push(IntegrationKeys.Gong)
        }
      }
    } else {
      // If integrations is not an object or is an array, add both to disconnected
      if (!disconnectedArray.includes(IntegrationKeys.Slack)) {
        disconnectedArray.push(IntegrationKeys.Slack)
      }
      if (!disconnectedArray.includes(IntegrationKeys.Gong)) {
        disconnectedArray.push(IntegrationKeys.Gong)
      }
    }

    setIntegrationStatus(statusMap)
    setConnected(connectedArray)
  }

  // Handle OAuth code from redirects
  useEffect(() => {
    console.log('Integrations -- 🔥 useEffect', {
      searchParams: searchParams.toString(),
    })

    // Check for OAuth error first (e.g., user denied consent)
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')
    const state = searchParams.get('state')

    if (error) {
      // Handle OAuth error (e.g., user denied consent)
      handleOAuthError(error, errorDescription, {
        accessDenied: 'Access was denied. Please grant consent to connect the integration.',
        generic: 'Failed to connect integration. Please try again.',
      })

      // Clear URL parameters and stay on integrations page
      navigate('/dashboard/integrations', { replace: true })
      setIsLoading(false)
      return
    }

    // Skip if there's no code parameter in the URL
    const code = searchParams.get('code')
    if (!code) return

    console.log('Integrations -- 🔥 useEffect - code', code)

    // Skip if we've already processed this code
    if (processedCodes.current.has(code)) {
      console.log('Integrations -- 🔥 useEffect - code already processed', code)
      return
    }

    console.log('Integrations -- 🔥 useEffect - code not processed', code)

    const handleIntegrationCode = async () => {
      console.log('Integrations -- 🔥 useEffect - handleIntegrationCode')

      const state: IntegrationKey = searchParams.get('state') as IntegrationKey

      console.log('/handleIntegrationCode searchParams', searchParams)
      console.log('/handleIntegrationCode code', code)
      console.log('/handleIntegrationCode state', state)

      // Add this code to the set of processed codes
      processedCodes.current.add(code)

      // Reset toast shown flag
      toastShown.current = false

      try {
        // If the redirect came from Gong we need to call connectToGong (it has extra logic like opening the import dialog).
        // For every other integration we should go straight to the generic handleConnect flow.
        if (state === IntegrationKeys.Gong) {
          await connectToGong(code, true)
        } else {
          await handleConnect(state, code)
        }

        // Check if we came from onboarding modal
        const fromOnboarding = localStorage.getItem('oauth_from_onboarding')
        const integrationType = localStorage.getItem('oauth_integration_type')

        if (fromOnboarding === 'true') {
          // Clear the flags
          localStorage.removeItem('oauth_from_onboarding')
          localStorage.removeItem('oauth_integration_type')

          // Store success info for onboarding modal
          localStorage.setItem('oauth_success', 'true')
          localStorage.setItem('oauth_success_type', integrationType || state)

          // Navigate back to home onboarding
          // TODO: Figure out why we can't just do navigate() since it renders a blank page
          window.location.assign('/dashboard/home-new')
        } else {
          // Clear URL parameters to prevent re-rendering
          // TODO: Figure out why we can't just do navigate() since it renders app in "blank" state,
          //  e.g., user name is just "User", existing projects, meetings, etc. don't show up
          window.history.replaceState(null, '', '/dashboard/integrations')
          window.location.assign('/dashboard/integrations')
          setIsLoading(false)
        }
      } catch (error) {
        console.error('Error processing OAuth code:', error)

        // Check if we came from onboarding modal
        const fromOnboarding = localStorage.getItem('oauth_from_onboarding')

        if (fromOnboarding === 'true') {
          // Clear the flags
          localStorage.removeItem('oauth_from_onboarding')
          localStorage.removeItem('oauth_integration_type')

          // Store error info for onboarding modal
          localStorage.setItem('oauth_error', 'true')
          localStorage.setItem('oauth_error_message', error.message || 'Connection failed')

          // Navigate back to home onboarding
          navigate('/dashboard/home', { replace: true })
        } else {
          // Still clear URL parameters even if there's an error
          navigate('/dashboard/integrations', { replace: true })
        }
      }
    }

    handleIntegrationCode()
  }, [user.integrations, user.active_workspace, workspaceData, searchParams, navigate])

  // Function to reset initialization flags when integration status changes
  const resetInitializationFlags = () => {
    // Removed debug log: Resetting initialization flags
    forcedInitializationRun.current = false
    integrationsInitialized.current = false
    statusInitialized.current = false
  }

  // Function to connect to Gong
  const connectToGong = async (code: string, showGongDialogOnSuccess = false) => {
    try {
      setIsFetchingStatus((prev) => ({ ...prev, GO: true }))

      if (!code) {
        console.error('No code provided for Gong connection')
        return false // Explicitly return false when no code is provided
      }

      toastShown.current = false

      // Make API call to connect Gong first
      const response = await customFetch('/auth/connectIntegration', 'POST', { key: IntegrationKeys.Gong, code })

      // Handle API error
      if (response.error) {
        console.error('Error connecting to Gong:', response.error)

        if (!toastShown.current) {
          toast.error(`Failed to connect Gong: ${response.error.message || 'Unknown error'}`)
          toastShown.current = true
        }
        return false
      }

      // Update the workspace data
      refreshWorkspace(true)

      // Reset initialization flags
      resetInitializationFlags()

      // Show success message
      if (!toastShown.current) {
        toast.success('Gong connected successfully!')
        toastShown.current = true
      }

      // Ensure loader is hidden after successful connection
      setIsLoading(false)

      // Show dialog immediately if requested and connection was successful
      if (showGongDialogOnSuccess === true) {
        setShowGongImportDialog(true)
      }

      // Update AuthContext user object for personal integrations so that state in other hooks stays in sync
      if (personalIntegrations.includes(IntegrationKeys.Gong as IntegrationKey)) {
        setUser((prevUser) => {
          if (!prevUser || Object.keys(prevUser).length === 0) return prevUser
          return {
            ...prevUser,
            integrations: {
              ...(prevUser.integrations || {}),
              [IntegrationKeys.Gong]: true,
            },
          }
        })
      }

      // Immediately mark the integration as connected in the local status map so the UI updates
      setIntegrationStatus((prev) => ({ ...prev, [IntegrationKeysMap[IntegrationKeys.Gong]]: true }))

      return true
    } catch (error) {
      console.error('Error connecting to Gong:', error)

      if (!toastShown.current) {
        toast.error(`Failed to connect Gong: ${error.message}`)
        toastShown.current = true
      }
      return false
    } finally {
      setIsFetchingStatus((prev) => ({ ...prev, GO: false }))
    }
  }

  useEffect(() => {
    async function getProducts() {
      try {
        const response = await customFetch('/getProductRequests', 'GET', null)
        if (response.error) throw new Error(response.error)
        response.products.sort((a, b) => a.order - b.order)
        setProducts(response.products)
      } catch (error) {
        console.error('Error getting products:', error)
      }
    }

    getProducts()
  }, [])

  const handleRequest = async (id, name) => {
    try {
      const response = await customFetch('/createRequest', 'POST', { productId: id, productName: name })
      if (response.error) throw new Error(response.error)
      setProducts((prevProducts) =>
        prevProducts.map((product) => (product.id === id ? { ...product, requested: true } : product))
      )
    } catch (error) {
      console.error('Error getting products:', error)
    }
  }

  async function prepareHandleConnect(key, showDialog) {
    if (key === IntegrationKeys.Zoom) return zoomSignIn()
    if (key === IntegrationKeys.Slack) return slackSignIn()
    if (key === IntegrationKeys.Gong) return gongSignIn()
    if (showDialog) {
      if (key === IntegrationKeys.GoogleCalendar && integrationStatus[IntegrationKeys.MicrosoftCalendar])
        return setShowSwitchDialog(key)
      if (key === IntegrationKeys.MicrosoftCalendar && integrationStatus[IntegrationKeys.GoogleCalendar])
        return setShowSwitchDialog(key)
    } else {
      setShowSwitchDialog('')
    }
    if (key === IntegrationKeys.GoogleCalendar) {
      if (signInMethod === 'google') {
        await handleConnect(IntegrationKeys.GoogleCalendar, null)
        setIsLoading(false)
      } else return googleCalendarSignIn()
    }
    if (key === IntegrationKeys.MicrosoftCalendar) {
      if (signInMethod === 'microsoft') {
        await handleConnect(IntegrationKeys.MicrosoftCalendar, null)
        setIsLoading(false)
      } else return microsoftCalendarSignIn()
    }
  }

  const handleConnect = async (key: IntegrationKey, code: string) => {
    console.log(`handleConnect - key: ${key}, code: ${code}`)

    try {
      setSaving(true)

      try {
        const response = await customFetch('/auth/connectIntegration', 'POST', { code, key })
        if (response && !response.error) {
          console.log(`handleConnect - response: ${JSON.stringify(response, null, 2)}`)

          // Refresh workspace and recompute integration status so every card reflects the latest state
          resetInitializationFlags()
          refreshWorkspace(true)

          // Immediately recompute personal integration status from current user object
          initializeIntegrations()

          toast.success(`${getName(key)} successfully connected!`)

          // Special handling for Gong - prompt to import
          if (key === 'gong') {
            setTimeout(() => {
              forceShowGongImportDialog()
            }, 1000)
          }

          // Update AuthContext user object for personal integrations so that state in other hooks stays in sync
          if (personalIntegrations.includes(key as IntegrationKey)) {
            setUser((prevUser) => {
              if (!prevUser || Object.keys(prevUser).length === 0) return prevUser
              return {
                ...prevUser,
                integrations: {
                  ...(prevUser.integrations || {}),
                  [key]: true,
                },
              }
            })
          }

          // Immediately mark the integration as connected in the local status map so the UI updates
          // Need to find key since can be passed in query params, so needs to be validated
          setIntegrationStatus((prev) => {
            const integrationKey = IntegrationKeysMap[key]
            if (!integrationKey) return prev
            if (prev[integrationKey] === true) return prev
            return { ...prev, [integrationKey]: true }
          })
        } else {
          console.error(`handleConnect - error: ${JSON.stringify(response, null, 2)}`)
          throw new Error(response.error || response.msg || 'Unknown error occurred')
        }
      } catch (error) {
        console.error(`Error connecting ${key}:`, error)
        const errorMsg = error.message || `Failed to connect ${getName(key)}`

        toast.error(errorMsg)

        // Update UI state to reflect failed connection
        if (key === 'gong') {
          const updatedStatus = { ...integrationStatus }
          updatedStatus.Gong = false
          setIntegrationStatus(updatedStatus)
        }
      }
    } finally {
      setSaving(false)
    }
  }

  const disconnectIntegration = async (key) => {
    // Removed debug logs: Disconnecting integration, Integration key mapping
    toastShown.current = false

    // Set fetching status to true for this integration
    setIsFetchingStatus((prev) => ({ ...prev, [key]: true }))

    const backendKey = IntegrationKeys[key]
    // Removed debug log: Backend key for disconnection

    if (!backendKey) {
      console.error('Invalid integration key:', key)
      if (!toastShown.current) {
        toast.error(`Failed to disconnect: Invalid integration key`)
        toastShown.current = true
      }
      setIsFetchingStatus((prev) => ({ ...prev, [key]: false }))
      return false
    }

    try {
      // Make API call to disconnect the integration
      const response = await customFetch('/auth/disconnectIntegration', 'DELETE', { key: backendKey })

      // Reset initialization flags
      resetInitializationFlags()

      if (response && response.error) {
        throw new Error(response.error)
      }

      // Update local state based on integration type
      if (organizationWideIntegrations.includes(backendKey)) {
        // Update workspace integrations
        refreshWorkspace(true)
      } else {
        // Update user integrations
        setUser((prevUser) => {
          if (!prevUser) return prevUser

          return { ...prevUser, integrations: { ...prevUser.integrations, [backendKey]: false } }
        })
      }

      // Update the integration status
      setIntegrationStatus((prev) => ({ ...prev, [key]: false }))

      // Update connected and disconnected arrays
      setConnected((prev) => prev.filter((k) => k !== key))

      // Show success message
      if (!toastShown.current) {
        toast.success(`${getName(backendKey)} disconnected successfully!`)
        toastShown.current = true
      }

      return true
    } catch (error) {
      console.error(`Error disconnecting ${getName(backendKey)}:`, error)

      if (!toastShown.current) {
        toast.error(`Failed to disconnect ${getName(backendKey)}: ${error.message}`)
        toastShown.current = true
      }

      return false
    } finally {
      // Reset fetching status when operation completes
      setIsFetchingStatus((prev) => ({ ...prev, [key]: false }))
    }
  }

  // Helper function to sort integrations with connected ones first
  /**
   * Sorts a list of integrations with connected ones appearing first
   *
   * @param {Array<string>} integrationList - List of integration keys to sort
   * @returns {Array<string>} Sorted list with connected integrations first
   *
   * This function:
   * 1. Takes a list of integration keys (e.g., 'googleCalendar', 'slack')
   * 2. Determines if each integration is connected by checking:
   *    - User integrations for personal integrations (Google Calendar, Microsoft Calendar, Zoom)
   *    - Workspace integrations for organization-wide integrations (Slack, Gong)
   * 3. Sorts the list so connected integrations appear first
   * 4. Maintains the original order for integrations with the same connection status
   */
  const getSortedIntegrations = (integrationList) => {
    return [...integrationList].sort((a, b) => {
      // Get the backend keys
      const aBackendKey = IntegrationKeys[a]
      const bBackendKey = IntegrationKeys[b]

      // Check if this is a personal or org-wide integration
      const isPersonalA = personalIntegrations.includes(a)
      const isPersonalB = personalIntegrations.includes(b)

      // Get connection status from the right source
      let aConnected = false
      let bConnected = false

      if (isPersonalA) {
        // Personal integration - get from user.integrations
        aConnected = user.integrations && user.integrations[aBackendKey] === true
      } else {
        // Organization integration - get from workspaceData
        aConnected =
          workspaceData?.integrations &&
          typeof workspaceData.integrations === 'object' &&
          !Array.isArray(workspaceData.integrations) &&
          workspaceData.integrations[aBackendKey]?.connected === true
      }

      if (isPersonalB) {
        // Personal integration - get from user.integrations
        bConnected = user.integrations && user.integrations[bBackendKey] === true
      } else {
        // Organization integration - get from workspaceData
        bConnected =
          workspaceData?.integrations &&
          typeof workspaceData.integrations === 'object' &&
          !Array.isArray(workspaceData.integrations) &&
          workspaceData.integrations[bBackendKey]?.connected === true
      }

      // Connected integrations come first
      if (aConnected && !bConnected) return -1
      if (!aConnected && bConnected) return 1

      // If both have same connection status, maintain original order
      return 0
    })
  }

  const zoomSignIn = async () => {
    try {
      const response = await customFetch('/auth/getZoomUrl', 'POST', {})
      if (response.error) throw new Error(response.error)
      if (response.url) {
        // Add state=zoom parameter to the URL
        const redirectUrl = new URL(response.url)
        redirectUrl.searchParams.append('state', 'zoom')
        window.location.href = redirectUrl.toString()
      }
    } catch (error) {
      console.error('Error searching zoom:', error)
      toast.error('Error connecting')
    }
  }

  const slackSignIn = async () => {
    try {
      const response = await customFetch('/auth/getSlackUrl', 'POST', {})
      if (response.error) throw new Error(response.error)
      if (response.url) {
        // Add state=slack parameter to the URL
        const redirectUrl = new URL(response.url)
        redirectUrl.searchParams.append('state', 'slack')
        window.location.href = redirectUrl.toString()
      }
    } catch (error) {
      console.error('Error searching slack:', error)
      toast.error('Error connecting')
    }
  }

  const googleCalendarSignIn = async () => {
    ;(window as any).google.accounts.oauth2
      .initCodeClient({
        client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
        scope:
          'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/calendar.readonly',
        ux_mode: 'popup',
        callback: async (response) => {
          await handleConnect(IntegrationKeys.GoogleCalendar, response.code)
          setIsLoading(false)
        },
      })
      .requestCode()
  }

  const microsoftCalendarSignIn = async () => {
    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID
    const redirectUri = encodeURIComponent(import.meta.env.VITE_CLIENT_URL + '/dashboard/integrations')
    const state = encodeURIComponent(IntegrationKeys.MicrosoftCalendar)
    const scopes = encodeURIComponent('User.Read Calendars.Read offline_access')
    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${scopes}&response_mode=query&state=${state}`
  }

  const gongSignIn = async () => {
    try {
      // Reset toast shown flag
      toastShown.current = false

      // Get the Gong authorization URL using the new Gong API route
      const response = await customFetch('/gong/getAuthUrl', 'POST', {})

      if (response.error) throw new Error(response.error)

      if (response.url) {
        // Add state=gong parameter to identify this as a Gong redirect
        const redirectUrl = new URL(response.url)
        redirectUrl.searchParams.append('state', 'gong')

        // Clear any existing flags
        localStorage.removeItem('show_gong_import')
        localStorage.removeItem('gong_redirect_pending')

        // Set a flag to indicate we're expecting a Gong redirect
        localStorage.setItem('gong_redirect_pending', 'true')

        // Redirect to Gong authorization page
        window.location.href = redirectUrl.toString()
      } else {
        throw new Error('No authorization URL returned')
      }
    } catch (error) {
      console.error('Error getting Gong authorization URL:', error)
      toast.error('Failed to connect to Gong. Please try again.')
    }
  }

  // Handle Gong import with selected days
  const handleGongImport = async (days) => {
    try {
      // Reset toast shown flag
      toastShown.current = false

      // Get the workspace ID from the workspace object or from user.active_workspace
      const workspaceId = workspaceData?.id || user.active_workspace

      if (!workspaceId) {
        toast.error('No active workspace found. Please select a workspace first.')
        console.error('No workspace ID available for Gong import')
        return false
      }

      // Check if Gong is connected before trying to import
      const isGongConnected = workspaceData?.integrations?.gong?.connected === true
      if (!isGongConnected) {
        toast.error('Gong is not connected to this workspace. Please connect Gong first.')
        console.error('Gong is not connected to workspace:', workspaceId)

        // Close the dialog
        setShowGongImportDialog(false)

        // Prompt to connect Gong
        setTimeout(() => {
          const confirmConnect = window.confirm('Would you like to connect Gong now?')
          if (confirmConnect) {
            gongSignIn()
          }
        }, 500)

        return false
      }

      // Make API call to import Gong data using the correct endpoint
      const response = await customFetch('/gong/importGongCalls', 'POST', { days, workspaceId })

      if (response.error) {
        console.error('Error importing Gong data:', response.error)

        // Handle specific error cases
        if (response.error.includes('not found for workspace')) {
          throw new Error('Gong integration not found. Please reconnect Gong and try again.')
        }

        toast.error(`Failed to import Gong data: ${response.error || 'Unknown error'}`)
        throw new Error(response.error || 'Error importing Gong data')
      }

      // Show success message - only after API call is successful
      if (!toastShown.current) {
        if (days === 0) {
          toast.success('Gong import started. Future calls will be imported in the background.', { duration: 6000 })
        } else {
          toast.success(`Gong import started. Calls from the last ${days} days will be imported in the background.`, {
            duration: 6000,
          })
        }
        toastShown.current = true
      }

      // Close the dialog on success
      setShowGongImportDialog(false)

      return true
    } catch (error) {
      console.error('Error importing Gong data:', error)

      // Show error toast with specific message for Gong connection issues
      if (error.message && error.message.includes('Gong integration not found')) {
        toast.error('Gong integration not found. Please reconnect Gong and try again.')

        // Close the dialog
        setShowGongImportDialog(false)

        // Prompt to reconnect Gong
        setTimeout(() => {
          const confirmReconnect = window.confirm('Would you like to reconnect Gong now?')
          if (confirmReconnect) {
            gongSignIn()
          }
        }, 500)
      } else {
        toast.error(`Error importing Gong data: ${error.message}`)
      }

      return false
    }
  }

  // Function to force show the Gong import dialog via API
  const forceShowGongImportDialog = async () => {
    try {
      // Removed debug log: Forcing Gong import dialog to show

      // Simply show the dialog without making an API call
      setShowGongImportDialog(true)

      return true
    } catch (error) {
      console.error('Error showing Gong import dialog:', error)

      // Still show the dialog even if there's an error
      setShowGongImportDialog(true)

      return true
    }
  }

  // Reset initialization flags when active workspace changes
  useEffect(() => {
    // Removed debug log: Active workspace changed, resetting initialization flags
    integrationsInitialized.current = false
    statusInitialized.current = false
    gongActionsChecked.current = false
  }, [user.active_workspace])

  // Helper function to handle disconnection of integrations
  const handleDisconnect = async (key) => {
    try {
      return await disconnectIntegration(key)
    } catch (error) {
      console.error(`Error in handleDisconnect for ${key}:`, error)
      return false
    }
  }

  const loadIntegrationStatus = async () => {
    try {
      const res = await customFetch('/auth/getIntegrationStatus', 'GET', null)
      if (res && !res.error && res.status) {
        // update user.integrations
        setUser((prev) => {
          if (!prev || Object.keys(prev).length === 0) return prev
          return { ...prev, integrations: { ...(prev.integrations || {}), ...res.status } }
        })

        // map backend keys to frontend keys for status map
        const newStatus = { ...integrationStatus }
        Object.keys(res.status).forEach((k) => {
          const frontKey = IntegrationKeysMap[k]
          if (frontKey) newStatus[frontKey] = res.status[k]
        })
        setIntegrationStatus(newStatus)
      }
    } catch (err) {
      console.error('Error loading integration status', err)
    }
  }

  // call once on mount when user ready
  useEffect(() => {
    if (user && user.id) {
      loadIntegrationStatus()
    }
  }, [user?.id])

  return (
    <>
      <div className="ml-[39px] mt-[31px] flex flex-col gap-[37px]">
        {isLoading ? (
          <p>Loading integrations...</p>
        ) : (
          <>
            <div>
              <p className="mb-[15px] text-[#62708d] text-lg font-bold font-['Manrope'] leading-tight tracking-wide">
                For Myself
              </p>
              <div className="flex items-center gap-[19px] flex-wrap">
                {/* Show all personal integrations (both connected and disconnected) with connected ones first */}
                {getSortedIntegrations(personalIntegrations).map((key) => {
                  // Find the corresponding short key (G, M, Z, etc.) for this integration
                  const shortKey = Object.keys(IntegrationKeys).find((k) => IntegrationKeys[k] === key)

                  // Use the short key to check the integration status
                  const isConnected = shortKey ? integrationStatus[shortKey] : false

                  return (
                    <Card
                      key={key}
                      index={key}
                      connected={isConnected}
                      name={getName(key)}
                      description={getDescription(key)}
                      logo={getLogo(key)}
                      handleConnect={() => prepareHandleConnect(key, true)}
                      handleDisconnect={() => handleDisconnect(shortKey)}
                      handleImport={() => {}}
                    />
                  )
                })}
              </div>
            </div>

            {/* Only display the "For My Organization" section if the workspace is a partner */}
            {isPartner && (config.slack || config.gong) && (
              <div>
                <p className="mb-[15px] text-[#62708d] text-lg font-bold font-['Manrope'] leading-tight tracking-wide">
                  For My Organization
                </p>
                <div className="flex items-center gap-[19px] flex-wrap">
                  {/* Show all organization-wide integrations with connected ones first */}
                  {getSortedIntegrations(organizationWideIntegrations).map((key) => {
                    // Find the corresponding short key (S, GO, etc.) for this integration
                    const shortKey = Object.keys(IntegrationKeys).find((k) => IntegrationKeys[k] === key)

                    // Use the short key to check the integration status
                    const isConnected = shortKey ? integrationStatus[shortKey] === true : false

                    return (
                      <Card
                        key={key}
                        index={key}
                        connected={isConnected}
                        name={getName(key)}
                        description={getDescription(key)}
                        logo={getLogo(key)}
                        handleConnect={() => prepareHandleConnect(key, true)}
                        handleDisconnect={() => handleDisconnect(shortKey)}
                        handleImport={key === 'gong' && isConnected ? () => setShowGongImportDialog(true) : undefined}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            <div>
              <p className="mb-[15px] text-[#62708d] text-lg font-bold font-['Manrope'] leading-tight tracking-wide">
                Other Integrations
              </p>
              <div className="flex items-center gap-[19px] flex-wrap">
                {products.map((product) => (
                  <RequestCard
                    key={product.id}
                    requested={product.requested}
                    logo={<div dangerouslySetInnerHTML={{ __html: product.icon }} />}
                    name={product.name}
                    description={product.description}
                    handleRequest={() => handleRequest(product.id, product.name)}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      {showSwitchDialog
        ? (createPortal(
            <SwitchCalendarDialog
              handleClose={() => setShowSwitchDialog('')}
              handleSwitch={() => prepareHandleConnect(showSwitchDialog, false)}
            />,
            document.body
          ) as ReactPortal)
        : null}
      {showGongImportDialog
        ? (createPortal(
            <GongImportDialog handleClose={() => setShowGongImportDialog(false)} handleImport={handleGongImport} />,
            document.body
          ) as ReactPortal)
        : null}
    </>
  )
}

const strategies = {
  [IntegrationKeys.GoogleCalendar]: {
    name: 'Google Calendar',
    description: 'Connecting to Google Calendar allows the Evermuse bot to join meetings without being manually added.',
    getLogo: () => {
      return <GoogleIcon />
    },
  },
  [IntegrationKeys.MicrosoftCalendar]: {
    name: 'Microsoft Calendar',
    description:
      'Connecting to Microsoft Calendar allows the Evermuse bot to join meetings without being manually added.',
    getLogo: () => {
      return <MicrosoftIcon />
    },
  },
  [IntegrationKeys.Zoom]: {
    name: 'Zoom',
    description:
      'You must be connected to Zoom in order for the Evermuse bot to be able to join and analyze your meetings.',
    getLogo: () => {
      return <ZoomIcon />
    },
  },
  [IntegrationKeys.Slack]: {
    name: 'Slack',
    description:
      'You must be connected to Slack in order for the Evermuse bot to be able to join and analyze your channels.',
    getLogo: () => {
      return <SlackIcon />
    },
  },
  [IntegrationKeys.Gong]: {
    name: 'Gong.io',
    description: 'Connect to Gong to analyze your recorded meetings and get insights from your sales conversations.',
    getLogo: () => {
      return <GongIcon width="79" height="79" viewBox="0 0 93 100" />
    },
  },
}

/**
 * Gets the display name for an integration
 *
 * @param {string} key - The integration key (e.g., 'googleCalendar', 'slack')
 * @returns {string} The human-readable name of the integration
 */
function getName(key) {
  return strategies[key]?.name || ''
}

/**
 * Gets the description text for an integration
 *
 * @param {string} key - The integration key (e.g., 'googleCalendar', 'slack')
 * @returns {string} The description of the integration
 */
function getDescription(key) {
  return strategies[key]?.description || ''
}

/**
 * Gets the logo component for an integration
 *
 * @param {string} key - The integration key (e.g., 'googleCalendar', 'slack')
 * @returns {React.ReactNode} The logo component for the integration
 */
function getLogo(key) {
  const logo = strategies[key]?.getLogo()
  return logo
}
